import type { AuditLogEntry } from '@hemmafixa/shared';
import { useCallback } from 'react';
import { useI18n } from '@/i18n/context';

const MAX_JSON = 80;

/** Text as written (reasons and notes are already capped by the API); other values as short JSON. */
function show(value: unknown) {
  if (typeof value === 'string') return value;
  const text = JSON.stringify(value);
  return text.length > MAX_JSON ? `${text.slice(0, MAX_JSON)}…` : text;
}

/** "status: pending → verified", "reason: F-skatt saknas": the changed fields of an entry, one line each. */
export function auditChanges(entry: Pick<AuditLogEntry, 'before' | 'after'>): string[] {
  const before = (entry.before ?? {}) as Record<string, unknown>;
  const after = (entry.after ?? {}) as Record<string, unknown>;
  // The database stores JSON keys in its own order; the status change reads best first
  const entries = Object.entries(after).sort(([a], [b]) => Number(b === 'status') - Number(a === 'status'));
  return entries.map(([key, value]) =>
    key in before ? `${key}: ${show(before[key])} → ${show(value)}` : `${key}: ${show(value)}`,
  );
}

/** Where an entry's subject is shown in the admin area, if anywhere. */
export function auditTarget(entry: Pick<AuditLogEntry, 'entityType' | 'entityId'>): string | null {
  switch (entry.entityType) {
    case 'user':
      return `/admin/users/${entry.entityId}`;
    case 'provider':
      return `/admin/providers/${entry.entityId}`;
    case 'job':
      return `/admin/review/${entry.entityId}`;
    case 'report':
      return `/admin/reports/${entry.entityId}`;
    default:
      return null;
  }
}

/** The action in words ("Verified the company"); the raw action for one without a text yet. */
export function useAuditAction() {
  const { t } = useI18n();
  return useCallback((action: string) => t.admin.audit.actions[action] ?? action, [t]);
}
